import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useLead, useUpdateLead } from '@saiens/shared/hooks';
import { toast } from '@saiens/shared/stores';
import { getUsers } from '@saiens/shared/api';
import type { CrmLeadDetail } from '@saiens/shared/types';
import { formatCurrency, formatDate } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  User,
  Calendar,
  FileText,
  HardHat,
  Pencil,
  Search,
  Check,
  X,
} from 'lucide-react';

export function CrmDetailPage() {
  const { id } = useParams<{ id: string }>();
  const leadId = id ? parseInt(id, 10) : undefined;
  const navigate = useNavigate();

  const { data: lead, isLoading, error } = useLead(leadId);

  const handleCreateConstruction = (saleOrderId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/construction/create?saleOrderId=${saleOrderId}`);
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="px-4 py-6">
        <Link to="/crm" className="flex items-center gap-1 text-primary">
          <ArrowLeft className="h-4 w-4" /> 返回列表
        </Link>
        <div className="mt-8 text-center text-red-500">載入失敗或專案不存在</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6">
      {/* Back button */}
      <Link to="/crm" className="mb-4 flex items-center gap-1 text-sm text-primary">
        <ArrowLeft className="h-4 w-4" /> 返回列表
      </Link>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">{lead.name}</h1>
        <div className="mt-2">
          <span className="text-lg font-semibold text-primary">
            {formatCurrency(lead.expected_revenue)}
          </span>
        </div>
      </div>

      {/* Info Cards */}
      <div className="space-y-4">
        {/* Contact Info */}
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 font-semibold text-gray-900">聯絡資訊</h2>
          <div className="space-y-2">
            {lead.partner_name && (
              <InfoRow icon={<User />} label="客戶" value={lead.partner_name} />
            )}
            {lead.contact_name && (
              <InfoRow icon={<User />} label="聯絡人" value={lead.contact_name} />
            )}
            {lead.phone && (
              <InfoRow icon={<Phone />} label="電話" value={lead.phone} isLink href={`tel:${lead.phone}`} />
            )}
            {lead.mobile && (
              <InfoRow icon={<Phone />} label="手機" value={lead.mobile} isLink href={`tel:${lead.mobile}`} />
            )}
            {lead.email_from && (
              <InfoRow icon={<Mail />} label="Email" value={lead.email_from} isLink href={`mailto:${lead.email_from}`} />
            )}
            {lead.project_address && (
              <InfoRow icon={<MapPin />} label="專案地址" value={lead.project_address} />
            )}
          </div>
        </div>

        {/* Project Info */}
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 font-semibold text-gray-900">專案資訊</h2>
          <div className="space-y-2">
            {lead.quotation_no && (
              <InfoRow icon={<FileText />} label="報價單號" value={lead.quotation_no} />
            )}
            {lead.user_name && (
              <OwnerRow lead={lead} />
            )}
            {lead.pm_user_name && (
              <InfoRow icon={<User />} label="專案經理" value={lead.pm_user_name} />
            )}
            {lead.hope_measure_date && (
              <InfoRow icon={<Calendar />} label="預計丈量" value={formatDate(lead.hope_measure_date)} />
            )}
            {lead.hope_install_date && (
              <InfoRow icon={<Calendar />} label="預計安裝" value={formatDate(lead.hope_install_date)} />
            )}
          </div>
        </div>

        {/* Related Orders */}
        {lead.sale_orders && lead.sale_orders.length > 0 && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-semibold text-gray-900">相關訂單 ({lead.sale_order_count})</h2>
            <div className="space-y-2">
              {lead.sale_orders.map((order) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between rounded-lg bg-gray-50 p-3"
                >
                  <Link
                    to={`/sale/${order.id}`}
                    className="flex-1 truncate font-medium hover:text-primary"
                  >
                    {order.name}
                  </Link>
                  <div className="ml-2 flex items-center gap-2">
                    <span className="text-primary">{formatCurrency(order.amount_total)}</span>
                    <button
                      onClick={(e) => handleCreateConstruction(order.id, e)}
                      className="flex items-center gap-1 rounded-lg bg-orange-500 px-2 py-1 text-xs text-white hover:bg-orange-600"
                      title="建立施工單"
                    >
                      <HardHat className="h-3 w-3" />
                      建施工單
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        {lead.description && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-semibold text-gray-900">備註</h2>
            <p className="whitespace-pre-wrap text-gray-600">{lead.description}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
  isLink,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  isLink?: boolean;
  href?: string;
}) {
  const content = (
    <>
      <div className="flex items-center gap-2 text-gray-400">
        <span className="h-4 w-4">{icon}</span>
        <span className="text-sm">{label}</span>
      </div>
      <span className={`font-medium ${isLink ? 'text-primary' : 'text-gray-900'}`}>{value}</span>
    </>
  );

  if (isLink && href) {
    return (
      <a href={href} className="flex items-center justify-between py-1">
        {content}
      </a>
    );
  }

  return <div className="flex items-center justify-between py-1">{content}</div>;
}

/**
 * 業務欄位＋變更入口。
 *
 * 只有後端判定有權的人（現任業務、建立者、負責 PM、中控、訓練官、業務主管）
 * 才看得到鉛筆；真正的閘門在後端 _can_change_owner_by。
 */
function OwnerRow({ lead }: { lead: CrmLeadDetail }) {
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState('');
  const updateLead = useUpdateLead();

  const { data: salesUsers, isLoading, isError, error: loadError } = useQuery({
    queryKey: ['master', 'users', 'sales', search],
    queryFn: async () => {
      const result = await getUsers({ group: 'sales', search });
      if (!result.success) throw new Error(result.message || '無法載入業務清單');
      return result.data ?? [];
    },
    enabled: editing,
  });

  const handlePick = async (userId: number) => {
    try {
      await updateLead.mutateAsync({ leadId: lead.id, data: { user_id: userId } });
      toast.success('負責業務已變更');
      setEditing(false);
    } catch {
      // 失敗訊息由 useUpdateLead 的 onError 統一顯示；這裡再 toast 一次
      // 會重複，而且 ApiError 不是 Error 實例，只會退化成泛化文字。
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between py-1">
        <div className="flex items-center gap-2 text-gray-400">
          <span className="h-4 w-4">
            <User />
          </span>
          <span className="text-sm">業務</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{lead.user_name}</span>
          {lead.can_change_owner && (
            <button
              type="button"
              aria-label={editing ? '取消變更負責業務' : '變更負責業務'}
              onClick={() => setEditing((open) => !open)}
              className="flex h-8 w-8 items-center justify-center text-gray-400"
            >
              {editing ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      {editing && (
        <div className="mt-2 rounded-lg border border-gray-200 p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="min-h-11 w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-base text-gray-900 outline-none focus:border-primary"
              placeholder="搜尋業務姓名"
            />
          </div>
          {isLoading ? (
            <div className="py-4">
              <LoadingSpinner />
            </div>
          ) : isError ? (
            <p className="mt-2 bg-red-50 px-3 py-2 text-sm font-medium text-red-600">
              {loadError instanceof Error ? loadError.message : '無法載入業務清單'}
            </p>
          ) : (
            <ul className="mt-2 max-h-64 overflow-y-auto">
              {(salesUsers ?? []).map((user) => (
                <li key={user.id}>
                  <button
                    type="button"
                    disabled={updateLead.isPending || user.id === lead.user_id}
                    onClick={() => handlePick(user.id)}
                    className="flex min-h-11 w-full items-center justify-between px-1 text-left text-base text-gray-900 disabled:opacity-40"
                  >
                    {user.name}
                    {user.id === lead.user_id && <Check className="h-4 w-4 text-primary" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
